// Mirrors reporting-hub's ReportingHistoryPage: a table of every reporting,
// with Open/Reproduce/Duplicate/Delete row actions. Auth/user identity is
// entirely exaport's own (req.aws_payload.email server-side, axiosWithAuth
// client-side) - no separate login/name-entry step like reporting-hub's own
// LandingPage, since exaport already knows who's logged in. Reproduce and
// Duplicate are jobs (see jobs.js) - progress is shown above the table, the
// list refreshes once they finish, and Reproduce's comparison is shown in a
// dialog before opening the new reporting.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import JobProgress from '../JobProgress';
import PageHeader from '../../common/PageHeader';
import EmptyState from '../../common/EmptyState';
import { apiErrorDetail, useJobRunner } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const STAGE_SEVERITY = {
  created: 'secondary',
  preflight: 'info',
  calculated: 'warning',
  reported: 'success',
};

// Stage severity -> ui-kit status-pill modifier (presentation only).
const STAGE_STATUS_CLASS = {
  secondary: '',
  info: 'ex-status-info',
  warning: 'ex-status-warning',
  success: 'ex-status-success',
};

const formatDate = (input) => {
  if (!input) return '';
  const date = new Date(input);
  return isNaN(date.getTime()) ? input : date.toLocaleString();
};

const formatValue = (v) => (typeof v === 'string' ? v : JSON.stringify(v));

// Result of a Reproduce job: does the reproduced reporting's output match
// the original's (preflight report / calculation result / fund report)?
const ReproductionComparisonDialog = ({ result, onHide, onOpen }) => {
  const comparison = result && result.comparison;
  const differences = (comparison && comparison.differences) || [];
  return (
    <Dialog header="Reproduction Result" visible={!!result} onHide={onHide} style={{ width: '42rem', maxWidth: '95vw' }} maximizable>
      {comparison && (
        <div className="flex flex-column gap-2">
          <div className="flex flex-wrap align-items-center gap-2">
            <Tag severity={comparison.match ? 'success' : 'danger'} value={comparison.match ? 'Outputs match' : `${differences.length} difference(s)`} />
            {comparison.input_integrity && (
              <Tag
                severity={comparison.input_integrity.ok ? 'success' : 'danger'}
                value={comparison.input_integrity.ok ? 'Inputs intact' : 'Input integrity issues'}
              />
            )}
            {comparison.code_hash && comparison.code_hash.changed && <Tag severity="warning" value="Code changed since original" />}
          </div>
          {comparison.files_compared && (
            <div className="cr-muted-text">Compared: {comparison.files_compared.join(', ') || 'none'}</div>
          )}
          {comparison.input_integrity && !comparison.input_integrity.ok && (
            <ul className="cr-compact-list">
              {(comparison.input_integrity.issues || []).map((issue, i) => (
                <li key={i}>{formatValue(issue)}</li>
              ))}
            </ul>
          )}
          {differences.length > 0 && (
            <DataTable value={differences.slice(0, 200)} size="small" showGridlines stripedRows paginator rows={10} responsiveLayout="scroll">
              <Column field="path" header="Path" />
              <Column header="Original" body={(d) => formatValue(d.original)} />
              <Column header="Reproduced" body={(d) => formatValue(d.reproduced)} />
            </DataTable>
          )}
        </div>
      )}
      <div className="cr-dialog-actions">
        <Button label="Close" className="p-button-secondary p-button-outlined" onClick={onHide} />
        {result && <Button label="Open reproduced reporting" onClick={() => onOpen(result.new_reporting_id)} />}
      </div>
    </Dialog>
  );
};

const ReportingHistoryPage = ({ onOpen, onOpenSettings, setLoading = () => {} }) => {
  const [reportings, setReportings] = useState([]);
  const [error, setError] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null); // {new_reporting_id, comparison}
  const { job: activeJob, run: runJob } = useJobRunner();
  const toast = useRef(null);
  // Deletions this page started and is waiting on: id -> short label for the toasts.
  const pendingDeletes = useRef(new Map());

  const fetchReportings = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await axiosWithAuth.get('/compartment-report/reportings');
      const rows = res.data || [];
      setReportings(rows);
      setError(null);
      // Resolve deletions this page is waiting on: gone = done, error = failed.
      for (const [id, label] of pendingDeletes.current) {
        const row = rows.find((r) => r.reporting_id === id);
        if (!row) {
          pendingDeletes.current.delete(id);
          toast.current?.show({ severity: 'success', summary: 'Reporting deleted', detail: label, life: 4000 });
        } else if (row.deleting && row.deleting.error) {
          pendingDeletes.current.delete(id);
          toast.current?.show({
            severity: 'error',
            summary: 'Delete failed',
            detail: `${label}: ${row.deleting.error}. Use Retry delete to try again.`,
            sticky: true,
          });
        }
      }
    } catch (err) {
      console.error('[fetchReportings] error:', errorMessage(err));
      if (!quiet) setError(errorMessage(err, 'Failed to fetch reportings.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [setLoading]);

  // While any reporting is being deleted (by this page or anyone else), refresh
  // the list quietly every 2 s until it disappears.
  const anyDeleting = reportings.some((r) => r.deleting && !r.deleting.error);
  useEffect(() => {
    if (!anyDeleting && pendingDeletes.current.size === 0) return undefined;
    const timer = setInterval(() => fetchReportings({ quiet: true }), 2000);
    return () => clearInterval(timer);
  }, [anyDeleting, fetchReportings]);

  useEffect(() => {
    fetchReportings();
  }, [fetchReportings]);

  const handleReproduce = async (reportingId) => {
    setError(null);
    try {
      const job = await runJob(reportingId, 'reproduce', {});
      await fetchReportings();
      setComparisonResult(job.result);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[handleReproduce] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Reproduce failed.'));
    }
  };

  const confirmDuplicate = async () => {
    if (!duplicateTarget) return;
    const target = duplicateTarget;
    setDuplicateTarget(null);
    setError(null);
    try {
      const job = await runJob(target, 'duplicate', {});
      await fetchReportings();
      onOpen(job.result.new_reporting_id);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[confirmDuplicate] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Duplicate failed.'));
    }
  };

  // Deletion runs in the background on the server (202): close the dialog at
  // once, mark the row, and report start and end with toasts.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget;
    const label = `Reporting ${id.slice(0, 8)}`;
    setDeleteTarget(null);
    setError(null);
    setReportings((rows) => rows.map((r) => (r.reporting_id === id ? { ...r, deleting: { started_at: null, error: null } } : r)));
    try {
      await axiosWithAuth.delete(`/compartment-report/reportings/${id}`);
      pendingDeletes.current.set(id, label);
      toast.current?.show({ severity: 'info', summary: 'Deleting reporting', detail: `${label} is being deleted in the background.`, life: 4000 });
      await fetchReportings({ quiet: true });
    } catch (err) {
      console.error('[confirmDelete] error:', errorMessage(err));
      // 409 = a job is still running for this reporting.
      toast.current?.show({ severity: 'error', summary: 'Delete not started', detail: apiErrorDetail(err, 'Delete failed.'), life: 8000 });
      await fetchReportings({ quiet: true });
    }
  };

  const isRowDeleting = (row) => !!(row.deleting && !row.deleting.error);

  const actionsBody = (row) => (isRowDeleting(row) ? (
    <span className="ex-status">
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />
      Deleting…
    </span>
  ) : (
    <div className="cr-row-actions">
      <Button label="Open" className="p-button-sm" onClick={() => onOpen(row.reporting_id)} />
      <Button
        icon="pi pi-replay"
        aria-label="Reproduce"
        tooltip="Reproduce - re-run from the stored inputs and compare outputs"
        tooltipOptions={{ position: 'top' }}
        className="p-button-secondary p-button-text p-button-rounded p-button-sm"
        disabled={!!activeJob}
        loading={!!activeJob && activeJob.action === 'reproduce' && activeJob.reporting_id === row.reporting_id}
        onClick={() => handleReproduce(row.reporting_id)}
      />
      <Button
        icon="pi pi-copy"
        aria-label="Duplicate"
        tooltip="Duplicate"
        tooltipOptions={{ position: 'top' }}
        className="p-button-secondary p-button-text p-button-rounded p-button-sm"
        disabled={!!activeJob}
        loading={!!activeJob && activeJob.action === 'duplicate' && activeJob.reporting_id === row.reporting_id}
        onClick={() => setDuplicateTarget(row.reporting_id)}
      />
      {row.deleting && row.deleting.error ? (
        <Button
          className="p-button-danger p-button-outlined p-button-rounded p-button-sm cr-retry-delete"
          icon="pi pi-refresh"
          aria-label="Retry delete"
          tooltip="Retry delete (the last delete failed)"
          tooltipOptions={{ position: 'top' }}
          onClick={() => setDeleteTarget(row.reporting_id)}
        />
      ) : (
        <Button
          className="p-button-danger p-button-text p-button-rounded p-button-sm"
          icon="pi pi-trash"
          aria-label="Delete"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          onClick={() => setDeleteTarget(row.reporting_id)}
        />
      )}
    </div>
  ));

  return (
    <div className="content-container">
      <Toast ref={toast} />
      <PageHeader
        title="Compartment Report"
        subtitle="Monthly compartment reportings - select data, validate, calculate and publish."
        icon="pi-briefcase"
        actions={
          <>
            <Button label="Settings" icon="pi pi-cog" className="p-button-secondary p-button-outlined" onClick={onOpenSettings} />
            <Button label="New Reporting" icon="pi pi-plus" onClick={() => onOpen(null)} />
          </>
        }
      />

      {activeJob && <JobProgress job={activeJob} style={{ marginBottom: '1rem' }} />}
      {error && <div className="ex-inline-error mb-3">{error}</div>}

      <DataTable
        value={reportings}
        dataKey="reporting_id"
        paginator={reportings.length > 15}
        rows={15}
        responsiveLayout="scroll"
        className="cr-history-table"
        emptyMessage={
          <EmptyState
            icon="pi-briefcase"
            title="No reportings yet"
            message="Start a new reporting to upload or fetch a loan tape and run the preflight checks."
            action={<Button label="New Reporting" icon="pi pi-plus" onClick={() => onOpen(null)} />}
          />
        }
      >
        <Column
          field="reporting_id"
          header="Reporting ID"
          bodyClassName="cr-mono-cell"
          body={(row) => (
            <span className="cr-id-cell" title={row.reporting_id}>
              {row.reporting_id}
            </span>
          )}
          style={{ width: '11rem' }}
        />
        <Column
          field="user"
          header="User / Source"
          bodyClassName="cr-ellipsis-cell"
          body={(row) => (
            <>
              <div className="cr-cell-primary" title={row.user}>
                {row.user}
              </div>
              <div className="cr-cell-secondary">{row.source}</div>
            </>
          )}
        />
        <Column
          field="stage"
          header="Stage"
          body={(row) => <span className={`ex-status cr-cap ${STAGE_STATUS_CLASS[STAGE_SEVERITY[row.stage] || 'info'] || ''}`}>{row.stage}</span>}
        />
        <Column
          field="preflight_status"
          header="Preflight"
          body={(row) =>
            row.preflight_status ? (
              <span
                className={`ex-status cr-cap ${
                  row.preflight_status === 'pass' ? 'ex-status-success' : row.preflight_status === 'warning' ? 'ex-status-warning' : 'ex-status-danger'
                }`}
              >
                {row.preflight_status}
              </span>
            ) : (
              <span className="cr-muted-value">–</span>
            )
          }
        />
        <Column field="created_at" header="Created" bodyClassName="cr-date-cell" body={(row) => formatDate(row.created_at)} />
        <Column header="Actions" body={actionsBody} headerClassName="cr-actions-col" bodyClassName="cr-actions-col" />
      </DataTable>

      <Dialog
        header="Duplicate Reporting"
        visible={!!duplicateTarget}
        onHide={() => setDuplicateTarget(null)}
        style={{ width: '30rem' }}
        maximizable
      >
        <p>Create a byte-identical copy of this reporting under a new ID?</p>
        <div className="cr-dialog-actions">
          <Button label="Cancel" className="p-button-secondary p-button-outlined" onClick={() => setDuplicateTarget(null)} />
          <Button label="Duplicate" onClick={confirmDuplicate} />
        </div>
      </Dialog>

      <ReproductionComparisonDialog
        result={comparisonResult}
        onHide={() => setComparisonResult(null)}
        onOpen={(id) => {
          setComparisonResult(null);
          onOpen(id);
        }}
      />

      <Dialog
        header="Delete Reporting"
        visible={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        style={{ width: '30rem' }}
        maximizable
      >
        <p>This permanently deletes the reporting and all its inputs/results. This cannot be undone. Deletion continues in the background; you can keep working.</p>
        <div className="cr-dialog-actions">
          <Button label="Cancel" className="p-button-secondary p-button-outlined" onClick={() => setDeleteTarget(null)} />
          <Button label="Delete" className="p-button-danger" onClick={confirmDelete} />
        </div>
      </Dialog>
    </div>
  );
};

export default ReportingHistoryPage;
